import {
  HSFormState,
  HSFormValues,
  useAppSelector,
  useDispatch,
  selectHSFormState,
  selectHSFormValues,
  noteHSFormValueUpdateRequested,
  selectHSFormShowRequiredFieldsDialog,
  noteHSFormRequiredFieldsDialogDismissed,
  selectIsHCAHardStops,
  selectIsUploaded,
  HSFormStateItem,
} from '@ax/redux-store';
import { FormGroup, FormField, FormLabel } from '@ax/ui';
import styles from './HSForm.module.scss';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import {
  HS_FORM_FIELD_RR,
  HS_FORM_FIELD_BP,
  HS_FORM_FIELD_HR,
  HS_FORM_FIELD_TEMP,
  HS_FORM_FIELD_SPO2,
  HS_FORM_FIELD_DIAGNOSIS,
  HS_FORM_FIELD_SI,
  HS_FORM_FIELD_CHIEF_COMPLAINT,
} from '@ax/constants';
import { HSInvalidFormModal } from '@/modules/Note/HSInvalidFormModal';
import { debounce } from '@/utils';

const DebouncedControlledInput = ({
  name,
  value,
  identifier,
  noteId,
  debounceTime,
  id,
}: {
  name: keyof HSFormValues;
  value: string;
  identifier: string;
  noteId: string;
  debounceTime: number;
  id: string;
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const debouncedOnChange = useCallback(
    debounce((v, noteId) => {
      dispatch(
        noteHSFormValueUpdateRequested({
          noteId: noteId as string,
          identifier,
          valueKey: name,
          value: v as string,
        }),
      );
    }, debounceTime),
    [dispatch, debounceTime],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedOnChange(e.target.value, noteId);
  };

  return (
    <input
      id={id}
      name={name}
      value={inputValue}
      onChange={handleChange}
      autoComplete={'off'}
    />
  );
};

export interface HSFormProps {
  noteId: string;
  readonly?: boolean;
}

const HSForm = ({ noteId, readonly }: Readonly<HSFormProps>) => {
  const dispatch = useDispatch();
  const formState = useAppSelector<HSFormState>(selectHSFormState);
  const formValues = useAppSelector<HSFormValues>(selectHSFormValues);
  const isHSFormEnabled = useAppSelector(selectIsHCAHardStops);
  const isNoteUploaded = useAppSelector(selectIsUploaded);
  const showRequiredFieldsDialog = useAppSelector<boolean>(selectHSFormShowRequiredFieldsDialog);

  if (!isHSFormEnabled) {
    return null;
  }

  const classesSmall = clsx({
    [styles.hs_form__field__small]: true,
    [styles.hs_form__disabled]: false,
  });

  const classesLarge = clsx({
    [styles.hs_form__field__large]: true,
    [styles.hs_form__disabled]: false,
  });

  const getFieldClasses = (field: HSFormStateItem) => {
    return clsx({
      [styles.hs_form__field__has_error]: formState.showErrors && field.error,
      [styles.hs_form__field__disabled]: formState.isDisabled || field.isLoading,
      [styles.hs_form__field__uneditable]: readonly || isNoteUploaded,
    });
  };

  const onHSInvalidFormModalClosed = () => {
    dispatch(noteHSFormRequiredFieldsDialogDismissed({ noteId }));
  };

  return (
    <>
      {
        <HSInvalidFormModal
          modalOpen={showRequiredFieldsDialog}
          modalClose={() => onHSInvalidFormModalClosed()}
        />
      }
      <form className={styles.hs_form}>
        <FormGroup className={clsx(classesSmall, getFieldClasses(formState.rr))}>
          <FormLabel htmlFor="rr">
            RR<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="rr"
              noteId={noteId}
              name="rr"
              value={formValues.rr}
              identifier={HS_FORM_FIELD_RR}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesSmall, getFieldClasses(formState.hr))}>
          <FormLabel htmlFor="hr">
            Pulse<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="hr"
              noteId={noteId}
              name="hr"
              value={formValues.hr}
              identifier={HS_FORM_FIELD_HR}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesSmall, getFieldClasses(formState.bp))}>
          <FormLabel htmlFor="bp">
            BP<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="bp"
              noteId={noteId}
              name="bp"
              value={formValues.bp}
              identifier={HS_FORM_FIELD_BP}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesSmall, getFieldClasses(formState.spo2))}>
          <FormLabel htmlFor="spo2">
            SpO2<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="spo2"
              noteId={noteId}
              name="spo2"
              value={formValues.spo2}
              identifier={HS_FORM_FIELD_SPO2}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesSmall, getFieldClasses(formState.temp))}>
          <FormLabel htmlFor="temp">
            Temp<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="temp"
              noteId={noteId}
              name="temp"
              value={formValues.temp}
              identifier={HS_FORM_FIELD_TEMP}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesLarge, getFieldClasses(formState.diagnosis))}>
          <FormLabel htmlFor="diagnosis">
            Diagnosis<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="diagnosis"
              noteId={noteId}
              name="diagnosis"
              value={formValues.diagnosis}
              identifier={HS_FORM_FIELD_DIAGNOSIS}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesLarge, getFieldClasses(formState.secondaryImpression))}>
          <FormLabel htmlFor="secondaryImpression">
            Secondary Impression<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="secondaryImpression"
              noteId={noteId}
              name="secondaryImpression"
              value={formValues.secondaryImpression}
              identifier={HS_FORM_FIELD_SI}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
        <FormGroup className={clsx(classesLarge, getFieldClasses(formState.chiefComplaint))}>
          <FormLabel htmlFor="chiefComplaint">
            Chief Complaint<span className={styles.hs_form__field__required_marker}>*</span>
          </FormLabel>
          <FormField>
            <DebouncedControlledInput
              id="chiefComplaint"
              noteId={noteId}
              name="chiefComplaint"
              value={formValues.chiefComplaint}
              identifier={HS_FORM_FIELD_CHIEF_COMPLAINT}
              debounceTime={1000}
            />
          </FormField>
        </FormGroup>
      </form>
    </>
  );
};

export default HSForm;
